import { PageLayout, Section } from "@/components/layout";
import { useClientTranslation } from "@/hooks/use-client-translation";
import { QrCode, Utensils, Link2, Check } from "lucide-react";

/* -------------------------------------------------------------------------- */
/*                              Section accents                               */
/* -------------------------------------------------------------------------- */

const ACCENTS = {
  scan: {
    icon: QrCode,
    gradient:
      "linear-gradient(135deg, #FFE25C 0%, #FF9D3F 100%)",
    dot: "linear-gradient(135deg, #FFE25C 0%, #FF9D3F 100%)",
    check: "text-[#C77B16]",
  },
  order: {
    icon: Utensils,
    gradient: "linear-gradient(135deg, #6B1BB8 0%, #510E90 100%)",
    dot: "#510E90",
    check: "text-purple-primary",
  },
  links: {
    icon: Link2,
    gradient:
      "linear-gradient(135deg, #7BE0B7 0%, #0F8A5F 100%)",
    dot: "linear-gradient(135deg, #7BE0B7 0%, #0F8A5F 100%)",
    check: "text-[#0F8A5F]",
  },
} as const;

type AccentKey = keyof typeof ACCENTS;

/* -------------------------------------------------------------------------- */
/*                                 Hero                                       */
/* -------------------------------------------------------------------------- */

function HeroSection() {
  const { t } = useClientTranslation();

  const pills: { key: AccentKey; label: string }[] = [
    { key: "scan", label: t("solutions.hero.pills.scan") },
    { key: "order", label: t("solutions.hero.pills.order") },
    { key: "links", label: t("solutions.hero.pills.links") },
  ];

  return (
    <Section className="px-0 py-12 md:py-20" padding="none">
      <div className="px-px-mobile md:px-px-tablet lg:px-px-desktop">
        <div className="mx-auto flex max-w-[820px] flex-col items-center gap-6 text-center">
          <span className="text-base font-semibold leading-relaxed text-foreground/70">
            {t("solutions.hero.eyebrow")}
          </span>
          <h1 className="text-[36px] font-medium leading-[110%] tracking-[-0.02em] md:text-5xl lg:text-[56px]">
            {t("solutions.hero.title")}
          </h1>
          <p className="max-w-[640px] text-lg font-normal leading-[155%] text-foreground/70">
            {t("solutions.hero.subtitle")}
          </p>
          <div className="mt-2 flex flex-wrap items-center justify-center gap-3">
            {pills.map((pill) => (
              <div
                key={pill.key}
                className="flex items-center gap-2.5 rounded-full border border-[rgba(0,0,0,0.1)] bg-white px-4 py-2 shadow-sm"
              >
                <span
                  className="h-3 w-3 shrink-0 rounded-full"
                  style={{ background: ACCENTS[pill.key].dot }}
                />
                <span className="text-sm font-medium leading-none text-foreground">
                  {pill.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Section>
  );
}

/* -------------------------------------------------------------------------- */
/*                            Product visual block                            */
/* -------------------------------------------------------------------------- */

function ProductVisual({
  accentKey,
  index,
  label,
}: {
  accentKey: AccentKey;
  index: string;
  label: string;
}) {
  const accent = ACCENTS[accentKey];
  const Icon = accent.icon;

  return (
    <div
      className="relative flex h-44 w-full overflow-hidden rounded-2xl sm:h-52 md:h-auto md:flex-1 md:self-stretch"
      style={{ background: accent.gradient }}
    >
      {/* Centered glass badge */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full border border-white/30 bg-white/20 backdrop-blur-sm md:h-24 md:w-24">
          <Icon className="h-7 w-7 text-white md:h-10 md:w-10" strokeWidth={1.75} />
        </div>
      </div>

      {/* Index label */}
      <span className="absolute bottom-5 left-5 text-xs font-semibold uppercase tracking-[0.18em] text-white/95 md:bottom-7 md:left-7">
        {index} — {label}
      </span>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                            Feature section                                 */
/* -------------------------------------------------------------------------- */

interface FeatureItem {
  title: string;
  description: string;
}

function FeatureSection({
  accentKey,
  index,
  imageSide,
}: {
  accentKey: AccentKey;
  index: string;
  imageSide: "left" | "right";
}) {
  const { t } = useClientTranslation();
  const accent = ACCENTS[accentKey];
  const ns = `solutions.${accentKey}`;

  const eyebrow = t(`${ns}.eyebrow`);
  const title = t(`${ns}.title`);
  const body = t(`${ns}.body`);
  const features = t(`${ns}.features`, { returnObjects: true }) as Record<
    string,
    FeatureItem
  >;
  const checklist = t(`${ns}.checklist`, { returnObjects: true }) as string[];

  const text = (
    <div className="flex flex-1 flex-col">
      <span className={`text-sm font-semibold uppercase tracking-[0.12em] ${accent.check}`}>
        {eyebrow}
      </span>
      <h2 className="mt-4 text-[28px] font-medium leading-[115%] tracking-[-0.015em] md:text-[40px]">
        {title}
      </h2>
      <p className="mt-5 text-[17px] font-normal leading-[160%] text-foreground/70">
        {body}
      </p>

      {/* Sub-features */}
      <div className="mt-8 flex flex-col gap-5">
        {Object.values(features).map((feature) => (
          <div key={feature.title} className="flex flex-col gap-1">
            <h3 className="text-base font-semibold leading-snug text-foreground">
              {feature.title}
            </h3>
            <p className="text-[15px] font-normal leading-[155%] text-foreground/65">
              {feature.description}
            </p>
          </div>
        ))}
      </div>

      {/* Divider */}
      <div className="my-8 h-px w-full bg-[rgba(0,0,0,0.1)]" />

      {/* Checklist */}
      <ul className="flex flex-col gap-4">
        {checklist.map((item) => (
          <li key={item} className="flex items-start gap-3">
            <Check className={`mt-0.5 h-4 w-4 shrink-0 ${accent.check}`} strokeWidth={2.5} />
            <span className="text-sm font-normal leading-snug text-foreground/80">
              {item}
            </span>
          </li>
        ))}
      </ul>

      {/* CTA */}
      <div className="mt-9">
        <a
          href="mailto:info@mezepay.com"
          className="inline-flex items-center justify-center rounded-full bg-black px-7 py-3 text-[15px] font-medium text-white transition-opacity hover:opacity-90"
        >
          {t("solutions.interested")}
        </a>
      </div>
    </div>
  );

  const visual = (
    <ProductVisual accentKey={accentKey} index={index} label={eyebrow} />
  );

  return (
    <Section className="px-0 border-t border-[rgba(0,0,0,0.1)] py-0" padding="none">
      <div className="px-px-mobile md:px-px-tablet lg:px-px-desktop">
        <div className="mx-auto max-w-[1280px] py-12 md:py-24">
          <div
            className={`flex flex-col-reverse items-stretch gap-7 md:gap-14 ${
              imageSide === "right" ? "md:flex-row" : "md:flex-row-reverse"
            }`}
          >
            {/* Mobile: block sits on top (flex-col-reverse). Desktop: side-by-side. */}
            {text}
            {visual}
          </div>
        </div>
      </div>
    </Section>
  );
}

/* -------------------------------------------------------------------------- */
/*                              Dark CTA band                                 */
/* -------------------------------------------------------------------------- */

function CtaSection() {
  const { t } = useClientTranslation();

  return (
    <Section className="px-0 py-12 md:py-20" padding="none">
      <div className="px-px-mobile md:px-px-tablet lg:px-px-desktop">
        <div className="mx-auto max-w-[1280px]">
          <div className="flex flex-col items-center gap-7 rounded-2xl bg-[#0E0B14] px-6 py-16 text-center md:px-16 md:py-20">
            <span className="text-xs font-semibold uppercase tracking-[0.18em] text-white/90">
              {t("solutions.cta.eyebrow")}
            </span>
            <h2 className="max-w-[760px] text-[28px] font-medium leading-[120%] tracking-[-0.015em] text-white md:text-[44px]">
              {t("solutions.cta.title")}
            </h2>
            <p className="max-w-[620px] text-[17px] font-normal leading-[160%] text-white/85">
              {t("solutions.cta.body")}
            </p>
            <a
              href="mailto:info@mezepay.com"
              className="mt-2 inline-flex items-center justify-center rounded-full bg-white px-8 py-3.5 text-[15px] font-medium text-black transition-opacity hover:opacity-90"
            >
              {t("solutions.cta.button")}
            </a>
          </div>
        </div>
      </div>
    </Section>
  );
}

/* -------------------------------------------------------------------------- */
/*                              Products Page                                 */
/* -------------------------------------------------------------------------- */

export default function SolutionsPage() {
  return (
    <PageLayout activePage="solutions">
      <main className="flex-1">
        <HeroSection />
        <FeatureSection accentKey="scan" index="01" imageSide="left" />
        <FeatureSection accentKey="order" index="02" imageSide="right" />
        <FeatureSection accentKey="links" index="03" imageSide="left" />
        <CtaSection />
      </main>
    </PageLayout>
  );
}
